import { useEffect, useId, type ReactNode } from 'react'
import { useUiStrings } from '@/hooks/useUiStrings'

interface ShareMinutesEmailSentModalProps {
  meetingTitle: string
  emails: string[]
  onClose: () => void
}

/**
 * Success confirmation after share-minutes email is sent.
 */
export default function ShareMinutesEmailSentModal({
  meetingTitle,
  emails,
  onClose,
}: ShareMinutesEmailSentModalProps) {
  const { t } = useUiStrings()
  const titleId = useId()
  const emailsLabel = emails.join(', ')

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        aria-label={t.close}
        className="fixed inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="animate-scale-in relative w-full max-w-sm rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 shadow-2xl sm:p-6">
        <div
          className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
          aria-hidden
        >
          <CheckIcon className="h-5 w-5" />
        </div>

        <h2 id={titleId} className="text-lg font-bold text-[var(--text-primary)]">
          {t.emailSentTitle}
        </h2>
        <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">
          {renderEmailSentBody(
            emails.length > 1 ? t.emailSentBodyMulti : t.emailSentBody,
            meetingTitle,
            emailsLabel
          )}
        </p>

        <button
          type="button"
          onClick={onClose}
          className="btn-primary mt-6 w-full px-4 py-2.5"
        >
          {t.done}
        </button>
      </div>
    </div>
  )
}

/** Inserts bold meeting name / emails into the localized template. */
function renderEmailSentBody(
  template: string,
  meetingName: string,
  emailsLabel: string
): ReactNode {
  const vars: Record<string, ReactNode> = {
    meetingName: (
      <strong className="font-semibold text-[var(--text-primary)]">{meetingName}</strong>
    ),
    email: <strong className="font-semibold text-[var(--text-primary)]">{emailsLabel}</strong>,
    emails: <strong className="font-semibold text-[var(--text-primary)]">{emailsLabel}</strong>,
  }
  const nodes: ReactNode[] = []
  const re = /\{(\w+)\}/g
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0
  while ((match = re.exec(template)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<span key={`t-${key++}`}>{template.slice(lastIndex, match.index)}</span>)
    }
    const value = vars[match[1]]
    nodes.push(
      value != null ? (
        <span key={`v-${key++}`}>{value}</span>
      ) : (
        <span key={`v-${key++}`}>{`{${match[1]}}`}</span>
      )
    )
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < template.length) {
    nodes.push(<span key={`t-${key++}`}>{template.slice(lastIndex)}</span>)
  }
  return nodes
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}
